import Cookie from 'js-cookie';
import oAuthClient from 'utils/auth/core';
import { isInWebView } from 'utils/isInWebView';
import { MOBILE_APP_GUEST } from '../constants';
import type { AuthMode } from './authMode';

export type SessionStatus = 'authenticated' | 'anonymous';

export type RequestAuth = {
  baseUrl: string;
  headers: Record<string, string>;
  credentials?: RequestCredentials;
};

/** What differs between the two Auth modes */
type AuthAdapter = {
  /** Base URL, headers and credentials for a backend request */
  requestAuth(): RequestAuth;
  /** Finds out whether the client is authenticated right now */
  checkStatus(): Promise<SessionStatus>;
  /** Keeps the session across a redirect to another site and back (PSD2 bank) */
  saveForExternalRedirect(): void;
  /** Ends the session on the backend; returns where to send the client next */
  endSession(redirectTo: string): Promise<string>;
  /** Where the client continues in the dashboard, still signed in */
  dashboardUrl(): Promise<string>;
};

const TOKEN_KEY = 'token';
/** Carries the Legacy token across the PSD2 bank redirect */
const RETURN_TRIP_COOKIE = 'token';
/** Written by older builds; never read */
const STALE_COOKIES = ['crossLoginToken'];

/** Legacy tokens are stored as base64("clientNumber:token") */
export function storeLegacyToken(username: string, token: string) {
  const authorizationToken = btoa(`${username}:${token}`);
  sessionStorage.setItem(TOKEN_KEY, authorizationToken);
  return authorizationToken;
}

/** Puts back a Legacy token that went through the PSD2 bank redirect */
export function restoreAfterExternalRedirect() {
  const token = Cookie.get(RETURN_TRIP_COOKIE);

  if (token && !sessionStorage.getItem(TOKEN_KEY)) {
    sessionStorage.setItem(TOKEN_KEY, token);
  }

  [RETURN_TRIP_COOKIE, ...STALE_COOKIES].forEach((name) => Cookie.remove(name));
}

/**
 * sessionStorage can be lost while the client is at the bank (the return may
 * open a new tab), so the token travels in a short-lived cookie.
 */
const carryTokenThroughRedirect = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);

  if (token) {
    Cookie.set(RETURN_TRIP_COOKIE, token, {
      expires: 10 / (24 * 60), // js-cookie counts days: 10 minutes
      secure: true,
      sameSite: 'strict',
    });
  }
};

/**
 * The gateway answers 401 without CORS headers, so a missing session shows up
 * as a network error (see docs/adr/0001 in dashboard-zaplo2-cz). Any other
 * answer means the gateway let the request through.
 */
async function checkKeycloakStatus(): Promise<SessionStatus> {
  let response: Response;

  try {
    response = await fetch(`${import.meta.env['VITE_WEB_API_URL']}/client`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return 'anonymous';
  }

  return response.status === 401 || response.status === 403
    ? 'anonymous'
    : 'authenticated';
}

const keycloak: AuthAdapter = {
  requestAuth: () => ({
    baseUrl: import.meta.env['VITE_WEB_API_URL'],
    headers: {},
    credentials: 'include',
  }),
  checkStatus: checkKeycloakStatus,
  // The session cookie survives the redirect by itself
  saveForExternalRedirect: () => {},
  endSession: async (redirectTo) => oAuthClient.createLogoutUrl(redirectTo),
  // The gateway session cookie signs the client in to the dashboard too
  dashboardUrl: async () => import.meta.env['VITE_DASHBOARD_URL'],
};

const legacyRequestAuth = (): RequestAuth => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Basic ${token}`;
  }

  return { baseUrl: import.meta.env['VITE_LEGACY_LOGIN_API'], headers };
};

const legacy: AuthAdapter = {
  requestAuth: legacyRequestAuth,
  checkStatus: async () =>
    sessionStorage.getItem(TOKEN_KEY) ? 'authenticated' : 'anonymous',
  saveForExternalRedirect: carryTokenThroughRedirect,
  endSession: async (redirectTo) => {
    const { baseUrl, headers } = legacyRequestAuth();

    if (headers.Authorization) {
      try {
        await fetch(`${baseUrl}/token/invalidate`, {
          method: 'DELETE',
          headers,
        });
      } catch {
        // The token is dropped locally either way
      }
    }

    return redirectTo;
  },
  // The dashboard signs the client in with a one-time token
  dashboardUrl: async () => {
    const { baseUrl, headers } = legacyRequestAuth();
    const response = await fetch(`${baseUrl}/client/security/one-time-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
    });

    if (!response.ok) {
      throw new Error(`one-time token: status ${response.status}`);
    }

    const { oneTimeToken }: { oneTimeToken?: string } = await response.json();

    if (!oneTimeToken) {
      throw new Error('one-time token: missing in response');
    }

    const queryString = window.location.search
      ? `&${window.location.search.substring(1)}`
      : '';
    const base = isInWebView()
      ? MOBILE_APP_GUEST
      : import.meta.env['VITE_DASHBOARD_URL'];

    return `${base}/one-time-token-login?token=${oneTimeToken}${queryString}`;
  },
};

export const adapters: Record<AuthMode, AuthAdapter> = { keycloak, legacy };
